import { useCallback, useEffect, useRef, useState } from 'react';
import type { SlotStatus } from '../PaymentStructureGrid/types';

export interface HookSlot<CellData> {
    id: string;
    batchKey: string;
    status: SlotStatus;
    data?: CellData;
}

export interface CellErrorEntry {
    id: string;
    error: true;
}

export type FetchBatchResponse<CellData> = Array<(CellData & { id: string }) | CellErrorEntry>;

export interface UseProgressiveFetchOptions<Item extends { id: string }, CellData> {
    items: Item[];
    enabled?: boolean;
    batchBy: (item: Item) => string;
    buildBatchBody: (batchKey: string, batch: Item[]) => unknown;
    fetchFn: (body: unknown, signal: AbortSignal) => Promise<FetchBatchResponse<CellData>>;
    onComplete?: () => void;
    timeout?: number;
}

export interface UseProgressiveFetchResult<CellData> {
    slots: HookSlot<CellData>[];
    retry: (batchKey: string) => void;
}

const DEFAULT_TIMEOUT_MS = 10000;

const isErrorEntry = <CellData>(entry: (CellData & { id: string }) | CellErrorEntry): entry is CellErrorEntry =>
    (entry as CellErrorEntry).error === true;

export const useProgressiveFetch = <Item extends { id: string }, CellData>(
    options: UseProgressiveFetchOptions<Item, CellData>
): UseProgressiveFetchResult<CellData> => {
    const { items, enabled = true } = options;

    // Keep options in a ref so the main effect doesn't depend on them (callers needn't memoize fetchFn/etc).
    const optsRef = useRef(options);
    optsRef.current = options;

    const buildInitialSlots = (its: Item[]): HookSlot<CellData>[] =>
        its.map((item) => ({ id: item.id, batchKey: optsRef.current.batchBy(item), status: 'pending' as const }));

    const [slots, setSlots] = useState<HookSlot<CellData>[]>(() => buildInitialSlots(items));

    const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
    const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const onCompleteFiredRef = useRef(false);
    // Ids fetched last run, so an `items` change re-fetches only cells with a new id (others are preserved).
    // Starts empty so the first run fetches everything.
    const prevItemIdsRef = useRef<Set<string>>(new Set());

    const groupByBatch = (its: Item[]): Map<string, Item[]> => {
        const map = new Map<string, Item[]>();
        for (const item of its) {
            const key = optsRef.current.batchBy(item);
            const arr = map.get(key) ?? [];
            arr.push(item);
            map.set(key, arr);
        }
        return map;
    };

    const cancelBatch = (batchKey: string): void => {
        abortControllersRef.current.get(batchKey)?.abort();
        abortControllersRef.current.delete(batchKey);
        const t = timeoutsRef.current.get(batchKey);
        if (t) clearTimeout(t);
        timeoutsRef.current.delete(batchKey);
    };

    const cancelAll = (): void => {
        abortControllersRef.current.forEach((c) => c.abort());
        abortControllersRef.current.clear();
        timeoutsRef.current.forEach((t) => clearTimeout(t));
        timeoutsRef.current.clear();
    };

    const fetchBatch = async (batchKey: string, batch: Item[]): Promise<void> => {
        cancelBatch(batchKey);

        const controller = new AbortController();
        abortControllersRef.current.set(batchKey, controller);

        const timeoutMs = optsRef.current.timeout ?? DEFAULT_TIMEOUT_MS;
        const timeoutId = setTimeout(() => {
            controller.abort();
            setSlots((prev) =>
                prev.map((slot) =>
                    slot.batchKey === batchKey && slot.status === 'pending' ? { ...slot, status: 'timed-out' as const } : slot
                )
            );
            timeoutsRef.current.delete(batchKey);
        }, timeoutMs);
        timeoutsRef.current.set(batchKey, timeoutId);

        try {
            const body = optsRef.current.buildBatchBody(batchKey, batch);
            const response = await optsRef.current.fetchFn(body, controller.signal);

            if (controller.signal.aborted) return;

            clearTimeout(timeoutId);
            timeoutsRef.current.delete(batchKey);

            const byId = new Map<string, (CellData & { id: string }) | CellErrorEntry>();
            for (const entry of response) {
                byId.set(entry.id, entry);
            }

            setSlots((prev) =>
                prev.map((slot) => {
                    if (slot.batchKey !== batchKey) return slot;
                    /* istanbul ignore next */
                    if (slot.status !== 'pending') return slot;
                    const entry = byId.get(slot.id);
                    if (!entry || isErrorEntry(entry)) {
                        return { ...slot, status: 'failed' as const };
                    }
                    return { ...slot, status: 'ready' as const, data: entry as CellData };
                })
            );
        } catch {
            if (controller.signal.aborted) return;

            clearTimeout(timeoutId);
            timeoutsRef.current.delete(batchKey);

            setSlots((prev) =>
                prev.map((slot) =>
                    slot.batchKey === batchKey && slot.status === 'pending' ? { ...slot, status: 'failed' as const } : slot
                )
            );
        }
    };

    // Abort on unmount only — not between re-runs, which must leave unchanged in-flight batches alone.
    useEffect(() => () => cancelAll(), []);

    useEffect(() => {
        onCompleteFiredRef.current = false;

        if (!enabled) {
            // Cancel in-flight work but KEEP prevItemIds — `enabled` only flips false after a load settles,
            // so re-enabling (e.g. on an edit) refetches just the changed cells, not the whole grid.
            cancelAll();
            return;
        }

        // Collect cells with a new id, grouped by batchKey, so each batch still fetches per down-payment row
        // (a term edit reloads just that column, a down-payment edit just that row); existing cells are kept.
        const changedByBatch = new Map<string, Item[]>();
        for (const item of items) {
            if (prevItemIdsRef.current.has(item.id)) continue;
            const batchKey = optsRef.current.batchBy(item);
            const batch = changedByBatch.get(batchKey) ?? [];
            batch.push(item);
            changedByBatch.set(batchKey, batch);
        }

        // Cancel in-flight work for batches that no longer exist (a removed/renamed down-payment row).
        const presentBatchKeys = new Set(items.map((item) => optsRef.current.batchBy(item)));
        [...abortControllersRef.current.keys()].forEach((batchKey) => {
            if (!presentBatchKeys.has(batchKey)) cancelBatch(batchKey);
        });

        // Preserve existing slots (status + data) by id; new ids -> pending; drop removed cells.
        setSlots((prev) => {
            const prevById = new Map(prev.map((slot) => [slot.id, slot]));
            return items.map((item) => {
                const existing = prevById.get(item.id);
                if (existing) return existing;
                return { id: item.id, batchKey: optsRef.current.batchBy(item), status: 'pending' as const };
            });
        });

        prevItemIdsRef.current = new Set(items.map((item) => item.id));

        for (const [batchKey, batch] of changedByBatch) {
            void fetchBatch(batchKey, batch);
        }
    }, [items, enabled]);

    useEffect(() => {
        /* istanbul ignore next */
        if (onCompleteFiredRef.current) return;
        if (slots.length === 0) return;
        const anyPending = slots.some((s) => s.status === 'pending');
        if (!anyPending) {
            onCompleteFiredRef.current = true;
            optsRef.current.onComplete?.();
        }
    }, [slots]);

    const retry = useCallback(
        (batchKey: string): void => {
            const grouped = groupByBatch(items);
            const batch = grouped.get(batchKey);
            if (!batch || batch.length === 0) return;

            onCompleteFiredRef.current = false;

            setSlots((prev) =>
                prev.map((slot) => (slot.batchKey === batchKey ? { ...slot, status: 'pending' as const, data: undefined } : slot))
            );

            void fetchBatch(batchKey, batch);
        },
        [items]
    );

    return { slots, retry };
};
